import {
  allPass,
  any,
  filter,
  pipe,
  isEmpty,
  anyPass,
  pathEq,
  prop,
  both,
  includes,
  values,
  either,
  reject,
  find,
  path,
  pathOr,
  has,
  propOr,
  gt,
  lt,
  __,
  equals,
  isNil,
  memoizeWith
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { getDiscountDisplayName } from '../../../utils/discount'
import {
  getConnectionFee,
  getEasyRoamOffers,
  getMobilityOffersWithItems,
  getMobilityPlan,
  getVASAddOns,
  getConnectionFeeAttribute,
  getMobVASAttributeList,
  getItemRecurringDiscounts,
  getItemsNonRecurringDiscounts,
  getMobilityWarranty,
  isTablet,
  isSmartwatch,
  getTerm,
  getOneTimeItemDiscounts,
  getLastMobilityAddon,
  getOrbitDeviceAttribute,
  getOrbitItemFromOffer,
  getItemRecurringCredits,
  getItemNonRecurringDiscounts
} from '../../../utils/mobility'
import { formatCurrency } from '../../../utils/currency'
import { getTermInMonths } from '../../molecules/CartItem/utils'
import { getCacheKey } from '../../../utils/cart'
import {
  buildConnectionFeeData,
  getColourAttributeText,
  getStorageAttributeText
} from '../../../utils/display'

const { getDisplayName, getMonthlyItemPrice, getItemOneTimePrice } = utils.cart

const _isOrbitDevice = pipe(
  prop('category'),
  either(equals('MOBILITY_TRADE_IN'), equals('MOBILITY_BRING_IT_BACK'))
)

const _hasOrbitOffer = pipe(prop('items'), values, any(_isOrbitDevice))

const hasTablet = pipe(prop('items'), values, any(isTablet))

const hasSmartwatch = pipe(prop('items'), values, any(isSmartwatch))

const getItemNonRecurringCreditsAndDiscounts = (item, term = 24) =>
  pipe(
    pathOr([], ['itemPrice', 'recurring']),
    find(has('alterations')),
    propOr([], 'alterations'),
    filter(
      anyPass([
        pathEq(['category'], 'CREDIT'),
        allPass([
          pipe(path(['recurringDuration']), gt(__, 1)),
          pipe(path(['recurringDuration']), lt(__, term))
        ])
      ])
    )
  )(item)

const buildDiscountMapping = (discounts, lang, item = {}, recurring = true) => {
  const recurringSuffix = recurring ? '/mo.' : ''

  const mappedDiscountDataList = discounts?.map((discount) => {
    const id = discount.id || discount.promotionId || item.id
    const hasCouponCode = discount.isCouponCode
    const prefixIcon = hasCouponCode ? 'PIGGYBANK' : 'DEALS'
    return {
      id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}${recurringSuffix}`,
      prefixIcon
    }
  })

  return mappedDiscountDataList
}

const isModem = pipe(
  allPass([
    pathEq(['category'], 'MOBILITY_TABLET'),
    pipe(prop('tags'), both(includes('connected_products'), includes('modem')))
  ])
)

const hasModem = pipe(prop('items'), values, any(isModem))

/**
 * @param {Object} cart - Cart response
 * @return {Object[]} A list of Connected Device mobility offers with sub-items data
 */
export const getConnectedDeviceOffers = memoizeWith(
  getCacheKey('MOB-Connected-Device'),
  pipe(
    getMobilityOffersWithItems,
    filter(anyPass([hasTablet, hasSmartwatch])),
    reject(_hasOrbitOffer)
  )
)

export const getMobilityInternetOffers = memoizeWith(
  getCacheKey('MOB-Internet-Offers'),
  pipe(getMobilityOffersWithItems, filter(anyPass([hasModem])), reject(_hasOrbitOffer))
)

export const getConnectedDeviceItem = (offer) =>
  pipe(prop('items'), values, find(anyPass([isTablet, isSmartwatch, isModem])))(offer)

export const getAttributes = (offer, device, translate) => {
  const plan = getMobilityPlan(offer)
  const mobilityVAS = getVASAddOns(offer)
  const warranty = getMobilityWarranty(offer)
  const connectionFee = getConnectionFee(offer)
  const easyRoam = getEasyRoamOffers(offer)

  // Storage and Colour
  const storageAttributeText = getStorageAttributeText(device, translate)
  const colourAttributeText = getColourAttributeText(device, translate)
  const deviceStorageAndColourLabel = storageAttributeText
    ? `${storageAttributeText}, ${colourAttributeText}`
    : `${colourAttributeText}`

  // Plan
  const easyPaymentLabel = isModem(device)
    ? ''
    : `, ${translate('item.easyPayment', { replacements: { duration: getTermInMonths(offer) } })}`
  const easyRoamLabel = isEmpty(easyRoam) ? '' : `, ${translate('item.easyRoam')}`
  const planNameLabel = `${getDisplayName(plan)}${easyPaymentLabel}${easyRoamLabel}`

  // Warranty
  const warrantyLabel = warranty ? getDisplayName(warranty) : null

  // Connection Fee
  const connectionFeeLabel = connectionFee
    ? getConnectionFeeAttribute(connectionFee, translate)
    : null

  // VAS
  const mobilityVASLabel = getMobVASAttributeList(mobilityVAS, translate)

  // Orbit
  const orbitOffer = getOrbitItemFromOffer(offer)
  const orbitLabel = getOrbitDeviceAttribute(orbitOffer, translate)

  return [
    deviceStorageAndColourLabel,
    planNameLabel,
    ...(warrantyLabel ? [warrantyLabel] : []),
    ...(connectionFeeLabel ? [connectionFeeLabel] : []),
    ...mobilityVASLabel,
    ...orbitLabel
  ]
}

export const getOneTimeEasyPaymentPriceBreakdown = (plan, device, translate, lang) => {
  if (isModem(device)) return null

  const deviceRecurringTotal = device?.totalPrice?.recurring?.[0]?.recurringBreakdowns?.[0]?.total
  const term = getTerm(plan)
  const name = translate('item.easyPaymentBalance', {
    replacements: { value: deviceRecurringTotal, duration: term }
  })

  const { credit } = getItemOneTimePrice(device)
  const price = `${formatCurrency(credit * -1, lang)}`

  return {
    id: device.id,
    name,
    price
  }
}

export const buildPlanPrice = (plan, lang) => {
  return {
    name: getDisplayName(plan),
    price: `${formatCurrency(getMonthlyItemPrice(plan).amount, lang)}/mo.`
  }
}

export const buildRecurringDiscountList = (device, plan, lang) => {
  const term = getTerm(plan)

  const deviceDiscounts = getItemRecurringDiscounts(device, term)
  const planDiscounts = getItemRecurringDiscounts(plan, term)

  const mappedPlanDiscounts = buildDiscountMapping(planDiscounts, lang, plan)
  const mappedDeviceDiscounts = buildDiscountMapping(deviceDiscounts, lang, device)

  return [...mappedPlanDiscounts, ...mappedDeviceDiscounts]
}

export const buildWarrantyInfoPrice = (offer, lang) => {
  const warranty = getMobilityWarranty(offer)

  if (!warranty) return null

  const warrantyMonthlyPrice = warranty?.itemPrice?.recurring?.[0]?.amount
  return {
    id: warranty.id,
    name: getDisplayName(warranty),
    price: `${formatCurrency(warrantyMonthlyPrice, lang)}/mo.`
  }
}

const buildNonVASMobilityAddon = (offer, translate, lang) => {
  const addon = getLastMobilityAddon(offer)

  if (!addon) return []

  return [
    {
      id: addon.id,
      name: translate('item.mobilityAddOn'),
      price: formatCurrency(getMonthlyItemPrice(addon).amount, lang)
    }
  ]
}

export const buildOtherChargesList = (offer, translate, lang) => {
  const connectionFee = buildConnectionFeeData(offer, translate, lang)
  const nonVasAddons = buildNonVASMobilityAddon(offer, translate, lang)
  return [connectionFee, ...nonVasAddons]
}

export const buildCreditsAndDiscountsList = (offer, plan, device, lang, isOrbit = false) => {
  const offerOneTimeDiscounts = getOneTimeItemDiscounts(offer)
  const planOneTimeDiscounts = getOneTimeItemDiscounts(plan)

  const planNonRecurringCreditsAndDiscounts = getItemNonRecurringCreditsAndDiscounts(plan)
  const deviceNonRecurringDiscounts = getItemNonRecurringCreditsAndDiscounts(device)

  const nonRecurringVASDiscounts = getItemsNonRecurringDiscounts(getVASAddOns(offer))

  if (isOrbit) {
    const term = getTerm(plan)
    const planNonRecurringDiscounts = getItemNonRecurringDiscounts(plan, term)
    const planRecurringCredits = getItemRecurringCredits(plan)
    const offerRecurringCredits = getItemRecurringCredits(offer)

    const orbitCreditsAndDiscounts = [
      ...planNonRecurringDiscounts,
      ...planRecurringCredits,
      ...offerRecurringCredits,
      ...nonRecurringVASDiscounts
    ]

    return buildDiscountMapping(orbitCreditsAndDiscounts, lang, {}, false)
  }

  const creditsAndDiscounts = [
    ...offerOneTimeDiscounts,
    ...planOneTimeDiscounts,
    ...planNonRecurringCreditsAndDiscounts,
    ...deviceNonRecurringDiscounts,
    ...nonRecurringVASDiscounts
  ]

  return buildDiscountMapping(creditsAndDiscounts, lang, {}, false)
}

/**
 * *** This calculation is required for connected devices ***
 * Be aware when refactoring
 * */
export const calculateDueTodaySubTotal = (device) => {
  const { amount, credit, discount } = getItemOneTimePrice(device) || {}

  return amount - credit - discount
}

// **** start of Orbit utils ****

export const getOrbitTabletOffers = memoizeWith(
  getCacheKey('MOB-Orbit-Tablet'),
  pipe(getMobilityOffersWithItems, filter(allPass([hasTablet, _hasOrbitOffer])))
)

export const shouldRenderConnectedDevices = (cart) => {
  const offers = getConnectedDeviceOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}
export const shouldRenderOrbitTablet = (cart) => {
  const offers = getOrbitTabletOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}
export const shouldRenderMobilityInternet = (cart) => {
  const offers = getMobilityInternetOffers(cart)
  return !isNil(offers) && !isEmpty(offers)
}
